import { useState, useEffect } from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { useConfig, useSession, UserType, useNextgenRouter } from '@nextgen-web-framework/all';
import { StorageService, formatDate, DateFormats } from '@nextgen-web-framework/core';
import type { AppState } from '@nextgen-web-framework/core';
import { useDispatch, useSelector } from 'react-redux';
import { ProgressiveProfile, RenewalCard } from '@commerce-profile-webcomponents/ui';
import { getAccountData } from '@commerce-webcomponents/sdk';
import { ProgressiveProfileNextStepEnum } from '@commerce-profile-webcomponents/sdk';
import { AccountOffersAndPoints, BonusPerformanceWithToggle } from '@commerce-webcomponents/ui';
import CmsComponent from '../CmsComponent/CmsComponent';
import UserInfo, { UserInfoSourceType } from '../UserInfo/UserInfo';
import styles from './MePanelContainer.module.scss';
import { MePanelContainerProps, RenewalCardCallbackType } from './types/propsTypes';

const MePanelContainer = ({ onCloseHandler, ...componentRestProps }: MePanelContainerProps) => {
  const dispatch = useDispatch();
  const router = useNextgenRouter();
  const { accountData, meData } = useSelector((state: AppState) => state.amshop);
  const { isAnonymous, session, isABO } = useSession();
  const [profileCompleted, setProfileCompleted] = useState(false);
  const cookie = StorageService?.getCookieStorage?.();
  const configs = useConfig();
  const profileTaskEnabled = configs.NEXT_PUBLIC_PROFILE_TASKS_ENABLED || false;
  const [expiryDate, setExpiryDate] = useState(session.accountExpirationDate);
  const [formattedExpiryDate, setFormattedExpiryDate] = useState('');

  useEffect(() => {
    const status = cookie.retrieve('tasks-completed');
    if (status === 'true') {
      setProfileCompleted(true);
    } else {
      setProfileCompleted(false);
    }
  }, []);

  useEffect(() => {
    if (session.type && !accountData.isLoaded) {
      dispatch(getAccountData({ userType: session.type as UserType }) as unknown as AnyAction);
    }
  }, [session.type]);
  const handleRenewalData = (data: RenewalCardCallbackType) => {
    setExpiryDate(data.expiryDate);
    setFormattedExpiryDate(data.formattedExpiryDate ?? '');
  };
  return (
    <div className={styles['me-panel-container-wrapper']} data-testid={'me-panel-container-testId'}>
      {!isAnonymous() && (
        <div className={styles['me-panel-userInfo-container']} data-testid={'me-panel-userInfo-testId'}>
          <UserInfo
            userName={session.userName ?? ''}
            expiryDate={
              formattedExpiryDate ||
              (expiryDate ? formatDate(expiryDate, DateFormats.SHORT_DATE, router.params?.locale as string) : '')
            }
            accountNo={session.aboNum ?? ''}
            sourceType={UserInfoSourceType.ME_PANEL}
          />
        </div>
      )}
      {!isAnonymous() && profileTaskEnabled && !profileCompleted && (
        <div className={styles['me-panel-profile-stepper-container']}>
          <ProgressiveProfile
            subtitleText={'profile.progressiveStepper.subheadingCommonHeading'}
            showHeading={true}
            nextStepType={ProgressiveProfileNextStepEnum.LINK}
          />
        </div>
      )}
      {!isAnonymous() && (
        <div className={styles['me-panel-renewal-container']}>
          <RenewalCard callback={handleRenewalData} />
        </div>
      )}
      {!isAnonymous() && (
        <div className={styles['me-panel-widget-container']} data-testid={'me-panel-widget-testId'}>
          <AccountOffersAndPoints
            couponAmt={accountData.couponAmt || 0}
            voucherAmt={accountData.voucherAmt || 0}
            amPointsAmt={accountData.amPointsAmt || 0}
            amPurseAmt={accountData.amPurseAmt || 0}
            isLoaded={accountData.isLoaded}
          />
          <div className={styles['me-panel-container-bottom-border']}></div>
          {isABO() && (
            <>
              <BonusPerformanceWithToggle />
              <div className={styles['me-panel-container-bottom-border']}></div>
            </>
          )}
        </div>
      )}

      <CmsComponent
        componentKey={'me_panel'}
        data={meData}
        {...{ ...componentRestProps, closeHandler: onCloseHandler }}
      />
    </div>
  );
};

export default MePanelContainer;
